import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * i18n context — single source of truth for the active language + translations.
 *
 * Three supported languages: English, French, Spanish.
 *
 * Exposes:
 *  • lang         — active language code, e.g. "en", "fr", "es"
 *  • setLang(c)   — switch the active language; persists to localStorage
 *  • t(key, fb?, params?)  — look up a translated string with placeholder substitution
 *  • languages    — list of {code, name, native_name, status} known to backend
 *  • loading      — true while strings or languages are being fetched
 *  • refresh()    — reload strings + languages
 *
 * Usage:
 *  ```jsx
 *  import { I18nProvider, useT } from "@/lib/i18n";
 *  function MyButton() {
 *    const t = useT();
 *    return <button>{t("action.save")}</button>;
 *  }
 *  ```
 */

const I18nContext = createContext(null);

const STORAGE_KEY = "usair_lang";
const DEFAULT_LANG = "en";
const SUPPORTED = new Set(["en", "fr", "es"]);

// Detect best initial language: localStorage > browser > 'en'. Anything that
// isn't en/fr/es falls back to English so old localStorage values from the
// removed AI-translation feature don't leave the UI in limbo.
function detectInitialLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.has(stored)) return stored;
    const nav = (navigator?.language || "en").slice(0, 2).toLowerCase();
    if (SUPPORTED.has(nav)) return nav;
  } catch {
    // localStorage may not be available (SSR / private mode)
  }
  return DEFAULT_LANG;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);
  const [strings, setStrings] = useState({});
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLanguages = useCallback(async () => {
    try {
      const r = await api.get("/i18n/languages");
      setLanguages(r.data?.languages || []);
    } catch {
      // Quiet fall-through
    }
  }, []);

  const loadStrings = useCallback(async (code) => {
    setLoading(true);
    try {
      const r = await api.get(`/i18n/strings/${encodeURIComponent(code)}`);
      setStrings(r.data?.strings || {});
    } catch {
      setStrings({});
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadLanguages();
    loadStrings(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync <html lang="…"> with the active language so screen readers behave.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!code || code === lang) return;
    if (!SUPPORTED.has(code)) return;
    setLangState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
    loadStrings(code);
    // iter150AZ.9 — best-effort persist to the user's profile so the
    // preference follows them across devices. Silent on 401 (signed
    // out) or other failures — localStorage is still authoritative.
    try {
      api.put("/me/locale", { language: code }).catch(() => {});
    } catch { /* ignore */ }
  }, [lang, loadStrings]);

  // iter150AZ.9 — once the user is authenticated AND they have no
  // stored language preference yet, fetch their effective locale from
  // the backend so first-login Quebec users see French automatically.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && SUPPORTED.has(stored)) return; // user already chose
        const r = await api.get("/me/locale");
        const effective = (r?.data?.effective || "").toLowerCase();
        if (!cancelled && SUPPORTED.has(effective) && effective !== lang) {
          setLangState(effective);
          try { localStorage.setItem(STORAGE_KEY, effective); } catch { /* ignore */ }
          loadStrings(effective);
        }
      } catch {
        // Not signed in or endpoint unavailable — keep client-side default.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(() => {
    loadLanguages();
    loadStrings(lang);
  }, [lang, loadLanguages, loadStrings]);

  /**
   * t(key, fallback?, params?) — translation lookup with placeholder substitution.
   * Placeholders use the {name} format: t("cart.pay_now", undefined, {amount: "$50"}).
   */
  const t = useCallback((key, fallback, params) => {
    const raw = strings[key] ?? fallback ?? key;
    if (!params) return raw;
    return Object.entries(params).reduce(
      (acc, [k, v]) => acc.split(`{${k}}`).join(String(v)),
      raw
    );
  }, [strings]);

  const value = useMemo(() => ({
    lang, setLang, t, languages, loading, refresh,
  }), [lang, setLang, t, languages, loading, refresh]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Read the full i18n object — `{lang, setLang, t, languages, …}`. */
export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    return {
      lang: "en",
      setLang: () => {},
      t: (k, fb) => fb ?? k,
      languages: [],
      loading: false,
      refresh: () => {},
    };
  }
  return ctx;
}

/** Convenience: get just the t() function. */
export function useT() {
  return useI18n().t;
}
